/**
 * BestOddsService - API service for the "Best Odds / Market Anomalies" module
 *
 * Mirrors SurebetService's conventions (request dedup, retry with backoff,
 * caching, HTTP error mapping) but targets the independent Best Odds product:
 * GET /api/core/anomalies (subscribers) and GET /api/core/anomalies/masked (public).
 */

import apiClient from '../../api/config/ApiClient';

class BestOddsService {
  /**
   * Cache configuration (subscriber-facing anomalies list)
   */
  static cache = {
    data: null,
    timestamp: null,
    ttl: 60000 // 1 minute cache TTL
  };

  /**
   * Masked preview cache (public landing page)
   */
  static maskedCache = {
    data: null,
    timestamp: null,
    ttl: 300000 // 5 minutes cache TTL for masked data
  };

  /**
   * Empty public preview payload (GET /api/core/anomalies/masked)
   */
  static EMPTY_MASKED_PREVIEW = {
    maskedItems: [],
    demoItem: null,
    totalActiveAnomalies: 0
  };

  /**
   * Request deduplication - prevent multiple simultaneous calls to same endpoint
   */
  static pendingRequests = new Map();

  /**
   * Retry configuration
   */
  static retryConfig = {
    maxRetries: 3,
    retryDelay: 2000, // 2 seconds
    retryableStatusCodes: [408, 429, 500, 502, 503, 504]
  };

  /**
   * Get the full anomalies list (subscribers only - 402 gated by backend)
   *
   * @param {Object} params - { useCache?: boolean }
   * @returns {Promise<{data: Array, error: string|null}>}
   */
  static async getAnomalies(params = {}) {
    const { useCache = true } = params;
    const endpoint = '/api/core/anomalies';

    try {
      if (useCache && this.isCacheValid()) {
        return { data: this.cache.data, error: null };
      }

      if (this.pendingRequests.has(endpoint)) {
        return await this.pendingRequests.get(endpoint);
      }

      const requestPromise = this.processAnomaliesRequest();
      this.pendingRequests.set(endpoint, requestPromise);

      const result = await requestPromise;
      this.pendingRequests.delete(endpoint);

      return result;
    } catch (error) {
      this.pendingRequests.delete(endpoint);
      return this.handleError(error);
    }
  }

  static async processAnomaliesRequest() {
    try {
      const responseData = await this.retryApiCall(async () => {
        return await apiClient.get('/api/core/anomalies', {
          minimalHeaders: true
        });
      });

      if (responseData === null || responseData === undefined) {
        return { data: [], error: null };
      }

      const transformed = this.transformResponse(responseData);
      this.updateCache(transformed);

      return { data: transformed, error: null };
    } catch (error) {
      this.dispatchSessionConflictIfNeeded(error);
      return this.handleError(error);
    }
  }

  /**
   * Get masked anomalies preview (public endpoint, landing page)
   *
   * @returns {Promise<{data: {maskedItems: Array, demoItem: Object|null, totalActiveAnomalies: number}, error: string|null}>}
   */
  static async getMaskedAnomalies() {
    const endpoint = '/api/core/anomalies/masked';

    try {
      if (this.isMaskedCacheValid()) {
        return { data: this.maskedCache.data, error: null };
      }

      if (this.pendingRequests.has(endpoint)) {
        return await this.pendingRequests.get(endpoint);
      }

      const requestPromise = this.processMaskedRequest();
      this.pendingRequests.set(endpoint, requestPromise);

      const result = await requestPromise;
      this.pendingRequests.delete(endpoint);

      return result;
    } catch (error) {
      this.pendingRequests.delete(endpoint);
      return { data: { ...this.EMPTY_MASKED_PREVIEW }, error: this.handleError(error).error };
    }
  }

  static async processMaskedRequest() {
    try {
      const responseData = await this.retryApiCall(async () => {
        return await apiClient.get('/api/core/anomalies/masked', {
          minimalHeaders: true
        });
      });

      if (responseData === null || responseData === undefined) {
        return { data: { ...this.EMPTY_MASKED_PREVIEW }, error: null };
      }

      const preview = this.transformMaskedPreviewResponse(responseData);
      this.updateMaskedCache(preview);

      return { data: preview, error: null };
    } catch (error) {
      this.dispatchSessionConflictIfNeeded(error);
      return { data: { ...this.EMPTY_MASKED_PREVIEW }, error: this.handleError(error).error };
    }
  }

  /**
   * Transform a single AnomalyDTO item
   */
  static transformAnomalyItem(item) {
    return {
      id: item.id,
      event: {
        id: item.event?.id || 0,
        date: item.event?.date || '',
        time: item.event?.time || '',
        teamOne: item.event?.teamOne || '',
        teamTwo: item.event?.teamTwo || '',
        championship: item.event?.championship || '',
        sport: item.event?.sport || ''
      },
      marketKey: item.marketKey || '',
      outcomeType: item.outcomeType || '',
      bestBookmaker: item.bestBookmaker || '',
      bestOdds: Number(item.bestOdds) || 0,
      marketAvg: Number(item.marketAvg) || 0,
      spread: Number(item.spread) || 0,
      bookmakerCount: Number(item.bookmakerCount) || 0,
      oddsLadder: Array.isArray(item.oddsLadder)
        ? item.oddsLadder.map((entry) => ({
          bookmaker: entry.bookmaker || '',
          value: Number(entry.value) || 0
        }))
        : []
    };
  }

  static transformResponse(data) {
    if (!Array.isArray(data)) {
      return [];
    }
    return data.map((item) => this.transformAnomalyItem(item));
  }

  /**
   * Transform PublicAnomaliesPreviewDto
   */
  static transformMaskedPreviewResponse(responseData) {
    const maskedItems = this.transformResponse(responseData.maskedItems || []);
    const demoItem = responseData.demoItem ? this.transformAnomalyItem(responseData.demoItem) : null;
    const totalActiveAnomalies = Number(responseData.totalActiveAnomalies);

    return {
      maskedItems,
      demoItem,
      totalActiveAnomalies: Number.isFinite(totalActiveAnomalies) && totalActiveAnomalies >= 0
        ? totalActiveAnomalies
        : maskedItems.length
    };
  }

  static dispatchSessionConflictIfNeeded(error) {
    if (error.response?.status !== 401) {
      return;
    }
    const errorData = error.response?.data || {};
    const detail = errorData.detail || errorData.message || '';
    const description = errorData.description || '';
    const combinedText = `${detail} ${description}`.toLowerCase();

    if (
      combinedText.includes('invalid or inactive token') ||
      combinedText.includes('inactive') ||
      combinedText.includes('revoked')
    ) {
      window.dispatchEvent(new CustomEvent('auth:sessionConflict', {
        detail: {
          reason: 'session_invalid',
          message: 'Your session is not valid or has been revoked. Please log in again.',
          endpoint: error.config?.url,
          status: 401,
          timestamp: new Date().toISOString(),
          errorData
        }
      }));
    }
  }

  static async retryApiCall(apiCallFn, retryCount = 0) {
    try {
      return await apiCallFn();
    } catch (error) {
      const isRetryable = this.isRetryableError(error);

      if (isRetryable && retryCount < this.retryConfig.maxRetries) {
        const delay = this.retryConfig.retryDelay * Math.pow(2, retryCount);
        await this.delay(delay);
        return await this.retryApiCall(apiCallFn, retryCount + 1);
      }

      throw error;
    }
  }

  static isRetryableError(error) {
    if (!error.response) {
      return true;
    }
    const statusCode = error.response?.status;
    return this.retryConfig.retryableStatusCodes.includes(statusCode);
  }

  static handleError(error) {
    console.error('BestOddsService error:', error);

    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 400:
          return { data: null, error: data?.detail || data?.message || 'Invalid request parameters' };
        case 401:
          return { data: null, error: 'Authentication required. Please log in.' };
        case 402:
          return { data: null, error: 'Active subscription required' };
        case 403:
          return { data: null, error: 'Access forbidden. You do not have permission.' };
        case 404:
          return { data: null, error: 'Service not found. Please try again later.' };
        case 429:
          return { data: null, error: 'Too many requests. Please slow down.' };
        case 500:
        case 502:
        case 503:
        case 504:
          return { data: null, error: 'Server error. Please try again later.' };
        default:
          return { data: null, error: data?.detail || data?.message || 'An unexpected error occurred' };
      }
    }

    if (error.request) {
      return { data: null, error: 'Network error. Please check your internet connection.' };
    }

    return { data: null, error: error.message || 'An unexpected error occurred' };
  }

  static isCacheValid() {
    if (!this.cache.data || !this.cache.timestamp) {
      return false;
    }
    return Date.now() - this.cache.timestamp < this.cache.ttl;
  }

  static isMaskedCacheValid() {
    if (!this.maskedCache.data || !this.maskedCache.timestamp) {
      return false;
    }
    return Date.now() - this.maskedCache.timestamp < this.maskedCache.ttl;
  }

  static updateCache(data) {
    this.cache.data = data;
    this.cache.timestamp = Date.now();
  }

  static updateMaskedCache(data) {
    this.maskedCache.data = data;
    this.maskedCache.timestamp = Date.now();
  }

  static clearCache() {
    this.cache.data = null;
    this.cache.timestamp = null;
  }

  static clearMaskedCache() {
    this.maskedCache.data = null;
    this.maskedCache.timestamp = null;
  }

  static delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

export default BestOddsService;
